import type React from 'react';
import { useCallback, useRef } from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Button, Text } from '@change/ds-core-ui';
import { ErrorMessage } from '@change/ds-forms';
import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate } from '@change-corgi/core/react/i18n';

import { photoPlaceholder, photoUploadControls, photoUploadLabel, photoUploadSection } from '../../../../shared/styles';

const FILE_INPUT_ID = 'endorsement-photo-input';

const PlaceholderIcon: React.FC = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39" fill="none">
		<path
			d="M30.3998 7.99999V30.4H7.9998V7.99999H30.3998ZM30.3998 4.79999H7.9998C6.2398 4.79999 4.7998 6.23999 4.7998 7.99999V30.4C4.7998 32.16 6.2398 33.6 7.9998 33.6H30.3998C32.1598 33.6 33.5998 32.16 33.5998 30.4V7.99999C33.5998 6.23999 32.1598 4.79999 30.3998 4.79999ZM22.6238 18.976L17.8238 25.168L14.3998 21.024L9.5998 27.2H28.7998L22.6238 18.976Z"
			fill="#766F81"
		/>
	</svg>
);

export type PhotoUploadSectionProps = {
	image: string | undefined;
	uppy: Uppy<Meta, Body>;
	isInvalid?: boolean;
	errorMessage?: string;
	uploadErrorMessage?: TranslationKey | null;
};

export const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({
	image,
	uppy,
	isInvalid = false,
	errorMessage,
	uploadErrorMessage,
}) => {
	const inputFileRef = useRef<HTMLInputElement>(null);

	const handleFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { files } = event.target;
			if (!files) return;

			const existingFiles = uppy.getFiles();
			existingFiles.forEach((existingFile) => {
				uppy.removeFile(existingFile.id);
			});

			Array.from(files).forEach((file) => {
				try {
					uppy.addFile({
						source: 'react:FileInput',
						name: file.name,
						type: file.type,
						data: file,
					});
				} catch {
					// errors handled in uppy abstraction
				}
			});

			// Reset input so same file can be selected again
			if (inputFileRef.current) {
				inputFileRef.current.value = '';
			}
		},
		[uppy],
	);

	return (
		<div className={photoUploadSection()}>
			<div className={photoUploadLabel()}>
				<Text variant="body" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_or_logo_label" />
				</Text>
				<Text variant="body" size="x-small" className="text-typography-weak">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_or_logo_text_support" />
				</Text>
			</div>

			<div className={photoUploadControls()}>
				<div
					className={cn(
						photoPlaceholder(),
						(isInvalid || !!uploadErrorMessage) && 'border-stroke-error-strong bg-fill-weaker border-2',
					)}
					data-testid="photo-placeholder"
				>
					{image ? (
						<img src={image} alt="" className="h-full w-full rounded-full object-cover" data-testid="uploaded-photo" />
					) : (
						<PlaceholderIcon />
					)}
				</div>

				<input
					ref={inputFileRef}
					type="file"
					onChange={handleFileChange}
					id={FILE_INPUT_ID}
					data-testid={FILE_INPUT_ID}
					data-qa={FILE_INPUT_ID}
					accept="image/*"
					className="sr-only"
				/>
				<Button
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{...({ as: 'label', htmlFor: FILE_INPUT_ID, role: '' } as unknown as EmptyProps)}
					variant="secondary-solid"
					size="small"
					data-testid="upload-photo-button"
					data-qa="upload-photo-button"
					className="cursor-pointer"
				>
					{image ? (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.change_photo_button" />
					) : (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.upload_photo_button" />
					)}
				</Button>
			</div>

			{uploadErrorMessage ? (
				<ErrorMessage size="small" className="mt-1" data-testid="upload-error-message">
					<Translate value={uploadErrorMessage} />
				</ErrorMessage>
			) : (
				errorMessage && (
					<ErrorMessage size="small" className="mt-1" data-testid="upload-error-message">
						{errorMessage}
					</ErrorMessage>
				)
			)}
		</div>
	);
};
